import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  X as CloseIcon,
  Search,
  MoreVertical,
  Copy,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import {
  Asset,
  NetworkType,
  getTokenBlockExplorerUrl,
  nativeTokenPseudoAddress,
} from '@mls-wallet/shared-types';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TokenLogo } from '@/components/shared/token-logo';
import { formatUsdValue } from '@/lib/utils';

const ITEMS_PER_PAGE = 20;

const isNativeTokenAsset = (asset: Asset) =>
  asset.tokenAddress.toLowerCase() === nativeTokenPseudoAddress.toLowerCase();

interface TokenSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  assets: Asset[];
  selectedToken?: Asset | null;
  onTokenSelect: (token: Asset) => void;
  getAssetBalance: (asset: Asset) => number;
  getAssetUsdValue: (asset: Asset) => number | null;
  network: NetworkType | null;
}

/**
 * Transaction builder token selector dialog
 */
export function TokenSelectorDialog({
  open,
  onOpenChange,
  isLoading = false,
  assets,
  selectedToken,
  onTokenSelect,
  getAssetBalance,
  getAssetUsdValue,
  network,
}: TokenSelectorDialogProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldBlockOnLoading = isLoading && assets.length === 0;

  // Filter assets by name or symbol based on search query
  const filteredAssets = useMemo(() => {
    // Case: No search query, return all assets
    if (!searchQuery) return assets;

    const query = searchQuery.toLowerCase();
    return assets.filter(
      (asset) =>
        asset.symbol.toLowerCase().includes(query) ||
        asset.name?.toLowerCase().includes(query)
    );
  }, [assets, searchQuery]);

  // Get the assets to display based on the visible count
  const displayedAssets = useMemo(() => {
    return filteredAssets.slice(0, visibleCount);
  }, [filteredAssets, visibleCount]);

  // useEffect to reset the visible count when the dialog is opened or the search query changes
  // This ensures we never display more assets than the user has scrolled to see
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);

    // Reset the scroll position to the top of the container
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [open, searchQuery]);

  /**
   * Manually handle scroll events to load more assets as the user scrolls
   * to the bottom of the container
   */
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      const scrollThreshold = 100;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Case: User has scrolled to the bottom of the container and there are more assets to load,
      // Increase the visible count by the ITEMS_PER_PAGE amount to load more assets
      if (
        distanceFromBottom < scrollThreshold &&
        visibleCount < filteredAssets.length
      ) {
        setVisibleCount((prev) =>
          Math.min(prev + ITEMS_PER_PAGE, filteredAssets.length)
        );
      }
    },
    [filteredAssets.length, visibleCount]
  );

  const tokenBalanceWithSymbol = (balance: number, symbol: string) => {
    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 6,
    }).format(balance);
    return `${formatted} ${symbol}`;
  };

  // Truncate a string to a maximum length and add an ellipsis if it is longer
  // Used for long token names or symbols (generally spam) to prevent overflow
  const truncateString = (str: string, maxLength: number) => {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  };

  // Handle token select by closing the dialog and calling the onTokenSelect callback
  const handleTokenSelect = (token: Asset) => {
    onTokenSelect(token);
    onOpenChange(false);
  };

  const handleCopyAddress = async (tokenId: string) => {
    await navigator.clipboard.writeText(tokenId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] p-0 gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-[32px] pt-[20px]">
          <DialogTitle className="font-semibold text-[16px] text-zinc-600">
            Select a token
          </DialogTitle>
          <button
            className="p-[15px] rounded-[58px] hover:bg-zinc-100 bg-transparent dark:hover:bg-zinc-800 transition-colors"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className="w-[18px] h-[18px]" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-[20px] pt-[22px]">
          <div className="bg-white dark:bg-zinc-950 border border-solid border-zinc-200 dark:border-zinc-800 rounded-[41px] h-[39px] relative">
            <div className="flex items-center gap-[10px] px-[22px] py-[10px] h-[39px]">
              <Search className="w-[16px] h-[16px] text-zinc-500 shrink-0" />
              <input
                type="text"
                placeholder="Search tokens"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none border-none font-normal text-[16px] text-zinc-900 dark:text-white placeholder:text-zinc-500"
              />
            </div>
          </div>
        </div>

        {/* Token List */}
        <div className="px-[20px] pt-[10px] pb-[20px]">
          <div
            key={searchQuery}
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="border border-solid border-zinc-200 dark:border-zinc-800 rounded-[10px] overflow-y-auto max-h-[400px]"
          >
            {shouldBlockOnLoading ? (
              <div className="px-[22px] py-[48px] flex flex-col items-center gap-3 text-zinc-500">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p>Loading token prices...</p>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="px-[22px] py-[18px] text-center text-zinc-500">
                No tokens found
              </div>
            ) : (
              <>
                {displayedAssets.map((asset, index) => {
                  const isSelected = selectedToken?.id === asset.id;
                  const isFirst = index === 0;
                  const isLast = index === displayedAssets.length - 1;
                  const isNative = isNativeTokenAsset(asset);
                  const explorerUrl =
                    !isNative && network
                      ? getTokenBlockExplorerUrl(asset.tokenAddress, network)
                      : null;

                  return (
                    <div
                      key={asset.id}
                      className={`w-full border-b border-solid border-zinc-100 dark:border-zinc-800 last:border-b-0 relative transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900 ${
                        isSelected
                          ? 'bg-slate-100 dark:bg-slate-900'
                          : 'bg-white dark:bg-zinc-950'
                      } ${isFirst ? 'rounded-tl-[10px] rounded-tr-[10px]' : ''} ${
                        isLast &&
                        displayedAssets.length === filteredAssets.length
                          ? 'rounded-bl-[10px] rounded-br-[10px]'
                          : ''
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleTokenSelect(asset)}
                        className="w-full flex flex-row items-center gap-0 px-[22px] py-[18px] rounded-[inherit]"
                      >
                        {/* Token Logo */}
                        <div className="flex items-center justify-center h-[40px] w-[40px] shrink-0 mr-[12px]">
                          <TokenLogo
                            symbol={asset.symbol}
                            logoUrl={asset.logoUrl}
                            size="xl"
                          />
                        </div>

                        {/* Token Info (fills space between logo & kebab) */}
                        <div className="flex-1 flex flex-col gap-[3px] items-start min-w-0 pl-0 pr-0">
                          {/* Symbol and Balance */}
                          <div className="w-full flex items-center justify-between font-medium">
                            <p className="text-[20px] text-black dark:text-white shrink-0 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                              {asset.symbol}
                            </p>
                            <p className="text-[14px] text-right text-zinc-600 dark:text-zinc-400 shrink-0 max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap">
                              {tokenBalanceWithSymbol(
                                getAssetBalance(asset),
                                truncateString(asset.symbol, 15)
                              )}
                            </p>
                          </div>

                          {/* Name and USD Value */}
                          <div className="w-full flex items-center justify-between text-[14px] text-zinc-600 dark:text-zinc-400">
                            <p className="font-medium shrink-0">
                              {truncateString(asset.name || asset.symbol, 40)}
                            </p>
                            {isLoading ? (
                              <div className="flex items-center justify-end shrink-0 min-w-[48px]">
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />
                              </div>
                            ) : (
                              <p className="font-normal text-right shrink-0">
                                {formatUsdValue(getAssetUsdValue(asset))}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Spacer keeps native-token rows aligned with rows that have a menu. */}
                        <div className="w-[56px] h-[40px] shrink-0" />
                      </button>

                      {!isNative && (
                        <>
                          <div className="absolute right-[22px] top-1/2 -translate-y-1/2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  aria-label={`Token actions for ${asset.symbol}`}
                                  className="p-[8px] rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <MoreVertical className="w-[24px] h-[24px] text-zinc-600 dark:text-zinc-400" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyAddress(asset.tokenAddress);
                                  }}
                                >
                                  <Copy className="mr-2 h-4 w-4" />
                                  <span>Copy address</span>
                                </DropdownMenuItem>
                                {explorerUrl && (
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        explorerUrl,
                                        '_blank',
                                        'noopener,noreferrer'
                                      );
                                    }}
                                  >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    <span>View in explorer</span>
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                {displayedAssets.length < filteredAssets.length && (
                  <div className="px-[22px] py-[18px] text-center text-zinc-500 bg-white dark:bg-zinc-950 border-t border-solid border-zinc-100 dark:border-zinc-800">
                    <p className="text-[14px]">
                      Showing {displayedAssets.length} of{' '}
                      {filteredAssets.length}{' '}
                      {searchQuery ? 'matching tokens' : 'tokens'}
                    </p>
                    <p className="text-[12px] mt-1 text-zinc-400">
                      Scroll down to load more
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
